import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Link,
  Chip,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowDownward as ArrowDownIcon,
  Description as FileIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const CommonTable = ({
  data = [],
  columns = [],
  searchable = true,
  selectable = true,
  onRowSelect = () => {},
  onSearch = () => {},
  searchPlaceholder = "Search",
  title = "Data Table",
  showTitle = true,
  showSearch = true,
  sortable = true,
  onSort = () => {},
  selectedRows = [],
  onSelectAll = () => {},
  onRowClick = () => {},
  rowClickable = false,
  customRowRenderer = null,
  customCellRenderer = null,
  tableProps = {},
  containerProps = {},
  isEdit = false,
  onEdit = () => {},
  editingRowId = null,
  onSave = () => {},
  onCancel = () => {},
  onEditChange = () => {},
  editFormData = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
    onSort(columnKey, direction);
  };

  const handleSelectAll = (event) => {
    const isSelected = event.target.checked;
    onSelectAll(isSelected);
  };

  const handleRowSelect = (rowId, isSelected) => {
    onRowSelect(rowId, isSelected);
  };

  const isAllSelected = selectedRows.length === data.length && data.length > 0;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < data.length;

  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    return columns.some((column) => {
      const value = row[column.key];
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  // Debug log
  console.log("CommonTable - data received:", data);
  console.log("CommonTable - filteredData:", filteredData);

  const renderCellContent = (row, column) => {
    if (customCellRenderer && customCellRenderer[column.key]) {
      return customCellRenderer[column.key](row, column);
    }

    // Check if column has a custom render function
    if (column.render) {
      return column.render(row);
    }

    const value = row[column.key];
    
    // Handle inline editing
    if (isEdit && editingRowId === row.id && column.editable !== false) {
      return (
        <TextField
          size="small"
          value={editFormData[column.key] || value || ""}
          onChange={(e) => onEditChange(column.key, e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "32px",
              fontSize: "14px",
            },
            "& .MuiInputBase-input": {
              padding: "4px 8px",
            },
          }}
        />
      );
    }
    
    if (column.type === "link") {
      return (
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            column.onClick && column.onClick(row);
          }}
          sx={{ color: "#1976d2", textDecoration: "underline" }}
        >
          {value}
        </Link>
      );
    }

    if (column.type === "chip") {
      return (
        <Chip
          label={value}
          size="small"
          color={column.chipColor || "default"}
          variant={column.chipVariant || "outlined"}
        />
      );
    }

    if (column.type === "checkbox") {
      return (
        <Checkbox
          checked={selectedRows.includes(row.id)}
          onChange={(e) => handleRowSelect(row.id, e.target.checked)}
          color="primary"
        />
      );
    }

    return value;
  };


  return (
    <Box sx={{ width: "100%" }}>
      {/* Header with Title and Search */}
      {(showTitle || showSearch) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            px: 0,
            py: 2,
          }}
        >
          {showTitle && (
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", fontSize: "18px" }}>
              {title}
            </Typography>
          )}
          {showSearch && searchable && (
            <TextField
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666", fontSize: "20px" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "300px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "40px",
                },
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                  fontSize: "14px",
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          backgroundColor: "white",
          ...containerProps,
        }}
      >
        <Table {...tableProps}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
              {selectable && (
                <TableCell 
                  padding="checkbox"
                  sx={{
                    padding: "12px 16px",
                    borderBottom: "2px solid #e0e0e0",
                    width: "50px",
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    color="primary"
                    sx={{ padding: "4px" }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    padding: "12px 16px",
                    borderBottom: "2px solid #e0e0e0",
                    fontSize: "14px",
                    backgroundColor: "#f8f9fa",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: column.maxWidth || "200px",
                  }}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={sortConfig.key === column.key}
                      direction={sortConfig.key === column.key ? sortConfig.direction : "asc"}
                      onClick={() => handleSort(column.key)}
                      IconComponent={ArrowDownIcon}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: "16px",
                          marginLeft: "4px",
                        },
                        "&:hover": {
                          color: "#1976d2",
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {isEdit && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    padding: "12px 16px",
                    borderBottom: "2px solid #e0e0e0",
                    fontSize: "14px",
                    backgroundColor: "#f8f9fa",
                    width: "80px",
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (isEdit ? 1 : 0)}
                  align="center"
                  sx={{ 
                    padding: "60px 40px", 
                    color: "#666",
                    border: "none"
                  }}
                >
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center", 
                    gap: 2 
                  }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      mb: 1
                    }}>
                      <FileIcon sx={{ fontSize: 64, color: "#ccc" }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: "#666", fontWeight: 500 }}>
                      No test cases uploaded
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#999" }}>
                      Upload or import test cases to get started
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  hover={rowClickable}
                  onClick={() => rowClickable && onRowClick(row)}
                  sx={{
                    cursor: rowClickable ? "pointer" : "default",
                    "&:nth-of-type(even)": {
                      backgroundColor: "#fafafa",
                    },
                    "&:hover": {
                      backgroundColor: rowClickable ? "#f5f5f5" : "inherit",
                    },
                  }}
                >
                  {selectable && (
                    <TableCell 
                      padding="checkbox"
                      sx={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e0e0e0",
                        width: "50px",
                      }}
                    >
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                        color="primary"
                        sx={{ padding: "4px" }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e0e0e0",
                        fontSize: "14px",
                        color: "#333",
                        maxWidth: column.maxWidth || "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={typeof renderCellContent(row, column) === 'string' ? renderCellContent(row, column) : ''}
                    >
                      {renderCellContent(row, column)}
                    </TableCell>
                  ))}
                  {isEdit && (
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #e0e0e0",
                        width: "80px",
                      }}
                    >
                      {editingRowId === row.id ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => onSave(row.id)}
                            sx={{
                              width: 20,
                              height: 20,
                              minWidth: 20,
                              backgroundColor: "#4CAF50",
                              borderRadius: 5,
                              padding: 0,
                              "&:hover": {
                                backgroundColor: "#45a049",
                              },
                              transition: "all 0.3s ease-out",
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 16, color: "white" }} />
                          </Button>
                          <Button
                            size="small"
                            onClick={() => onCancel()}
                            sx={{
                              width: 20,
                              height: 20,
                              minWidth: 20,
                              backgroundColor: "#f44336",
                              borderRadius: 5,
                              padding: 0,
                              "&:hover": {
                                backgroundColor: "#da190b",
                              },
                              transition: "all 0.3s ease-out",
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16, color: "white" }} />
                          </Button>
                        </Box>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(row);
                          }}
                          sx={{
                            padding: 0,
                            color: "#0094FB",
                            "&:hover": {
                              backgroundColor: "rgba(0, 148, 251, 0.04)",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CommonTable;
